<!-- eslint-disable max-len -->
<template>
  <aha-modal @hide="hideModal()" title="Invite members">
    <form @submit.prevent="handleSendInvitation()">
      <p v-if="false" class="text-base-80">
        Get your teammates on board to enhance collaboration.
      </p>
      <span class="flex items-center">
        <img
          src="@/assets/images/icon/mail.svg"
          style="filter: invert(31%) sepia(0%) saturate(1%) hue-rotate(175deg) brightness(84%) contrast(86%);"
          width="16" />
        <b class="ml-1">Emails:</b>
      </span>
      <AhaInputTag
        id="input-tag__invite-new-member"
        class="mt-1 mb-2"
        v-model="emails"
        :placeholder="emails.length > 0 ? '' : 'Enter emails, comma or space separated'"
        :validator="emailRegex"
        :disabled="isDisabledInviteeInput"
      />
      <div v-if="currentAccount.createdFrom !== 'presenter'">
        <div
          v-if="!isExpiredPlan"
          class="mb-2"
          :class="{
            'text-green-100': modalData.numberOfUsedLicenses < modalData.licenseQuantity,
          }"
        >
          <span class="font-bold">Licenses used: </span>
          <span>
            {{ modalData.numberOfUsedLicenses | formatNumber }}/{{ modalData.licenseQuantity | formatNumber }}
          </span>
        </div>
        <div
          v-if="isExpiredPlan"
          class="mb-2 font-bold text-red-50"
        >
          Your licences have expired
        </div>
        <p class="text-base-90" v-if="modalData.licenseQuantity > 1">
          For more licenses, please
          <a
            class="font-bold text-primary-90"
            target="_blank"
            :href="modalData.myTeamFooterURL"
            @click="handleClickOnFooterTypeForm()"
          >contact our Sales team</a>
          for the best deal.
        </p>
        <p class="text-base-90" v-if="modalData.licenseQuantity === 1">
          You need additional licenses to proceed. Please
          <a
            class="font-bold text-primary-90"
            target="_blank"
            :href="modalData.myTeamFooterURL"
            @click="handleClickOnFooterTypeForm()"
          >contact our Sales team</a>
          to get the best deal for your team today.
        </p>
      </div>
      <div v-if="showInviteError">
        <span class="block text-red-100 text-xs">{{ inviteErrorListEmail }}</span>
      </div>
      <div v-if="isShowMaxLimitSelfCreatTeamErr && currentAccount.createdFrom === 'presenter'">
        <span class="block text-red-100 text-xs">
          Your team has reached {{ currentAccount.licenseQuantity || 200 }} members. To add more, review your existing member list or
          <span class="underline cursor-pointer" @click="contactSupport">contact our support team.</span>
        </span>
      </div>
    </form>
    <template #action>
      <aha-link @click="hideModal()">
        <span class="font-bold">Cancel</span>
      </aha-link>
      <aha-button
        @click="handleSendInvitation()"
        ref="sendInvitationButton"
        has-margin-left
        type="submit"
        :disabled="isDisabledSendButton"
        :submitting="isSendingInvitations"
      >
        Send invitation
      </aha-button>
    </template>
  </aha-modal>
</template>

<script>
import moment from 'moment'
// inspiration: https://learnvue.co/2020/01/build-a-custom-vuejs-tag-input-in-under-10-minutes/
import { mapState } from 'vuex'
import InvitationApi from '@/api/invitation'
import utils from '@/util'
import gaCategories from '@/constant/gaCategories'

export default {
  props: {
    modalData: {
      type: Object,
      default: () => {},
    },
  },
  data() {
    return {
      emails: [],
      isSendingInvitations: false,
      showInviteError: false,
      inviteErrorListEmail: '',
      emailRegex: "^(([^<>()[\\]\\.,;:\\s@']+(\\.[^<>()[\\]\\.,;:\\s@']+)*)|('.+'))@((\\[[0-9]{1,3}\\.[0-9]{1,3}\\.[0-9]{1,3}\\.[0-9]{1,3}\\])|(([a-zA-Z\\-0-9]+\\.)+[a-zA-Z]{2,}))$",
    }
  },
  computed: {
    ...mapState({
      currentUser: (state) => state.currentUser,
      currentAccount: (state) => state.currentAccount,
    }),
    isDisabledInviteeInput() {
      if (this.modalData.numberOfUsedLicenses + this.emails.length
        >= this.modalData.licenseQuantity) return true
      return false
    },
    isDisabledSendButton() {
      const hasInput = this.isInputTagHasValue || this.emails.length > 0
      return !hasInput || (this.modalData.numberOfUsedLicenses >= this.modalData.licenseQuantity)
    },
    isShowMaxLimitSelfCreatTeamErr() {
      return (this.modalData.numberOfUsedLicenses >= this.modalData.licenseQuantity)
    },
    currentUserIsTeamAdmin() {
      return this.currentUser.accountId && this.currentUser.accountRole === 'admin'
    },
    isExpiredPlan() {
      return moment().isAfter(moment(this.modalData.planEndAt), 'minute')
    },
  },
  mounted() {
    setTimeout(() => {
      this.focusInviteeInput()
    }, 150)
  },
  methods: {
    focusInviteeInput() {
      const input = document.getElementById('input-tag__invite-new-member').children[0]
      if (!input) return
      input.focus()
    },

    handleClickOnFooterTypeForm() {
      if (this.currentUserIsTeamAdmin) {
        utils.gaTrackEvent(
          gaCategories.MY_TEAM,
          'Team management > Invite modal > Admin clicks on typeform link',
        )
      } else {
        utils.gaTrackEvent(
          gaCategories.MY_TEAM,
          'Team management > Invite modal > Casual user clicks on typeform link',
        )
      }
    },

    async handleSendInvitation() {
      if (this.isSendingInvitations) return
      if (!this.isInputTagHasValue && this.emails.length === 0) {
        utils.showToastError('Please enter member\'s email')
        this.focusInviteeInput()
        return
      }
      this.isSendingInvitations = true
      // Because we have 2 elements listen to the Enter key at the same
      // time (the form & the input tag),
      // setTimeout a bit for waiting the input tag (emails) to be added after Enter pressed
      setTimeout(async () => {
        try {
          utils.gaTrackEvent(
            gaCategories.MY_TEAM,
            'Team management > Invite member > Send invitation',
            this.emails.toString(),
            '',
            {
              plan: this.$store.state.currentUser.plan,
              isSelfCreatedTeam: this.$store.state.currentAccount.createdFrom === 'presenter',
            },
          )
          await InvitationApi.createInvitations({
            emails: this.emails.map((email) => email.toLowerCase()),
          })
          this.$emit('hide')
          this.$emit('updateInvitations', this.emails.length)
          utils.gaTrackEvent(
            gaCategories.MY_TEAM,
            'Team management > Invite member > Invited successfully',
            this.emails.toString(),
            '',
            {
              plan: this.$store.state.currentUser.plan,
              isSelfCreatedTeam: this.$store.state.currentAccount.createdFrom === 'presenter',
            },
          )
          utils.showToastSuccess('Invited successfully')
        } catch (error) {
          utils.gaTrackEvent(
            gaCategories.MY_TEAM,
            'Team management > Invite member > Invite failed',
            error.body.message,
            '', {
              isSelfCreatedTeam: this.$store.state.currentAccount.createdFrom === 'presenter',
            },
          )
          this.showInviteError = true
          if (error.body.emails) {
            if (this.$store.state.currentAccount.createdFrom === 'presenter') {
              this.inviteErrorListEmail = `This emails is already has team: ${error.body.emails.join(', ')}`
            } else this.inviteErrorListEmail = `This emails is already has team or plan: ${error.body.emails.join(', ')}`
          } else if (error.body.message) this.inviteErrorListEmail = error.body.message
          else utils.showToastError('Something went wrong, please check your connection or contact us for support')
        } finally {
          this.isSendingInvitations = false
        }
      }, 300)
    },
    hideModal() {
      utils.gaTrackEvent(
        gaCategories.MY_TEAM,
        'Team management > Invite member > Cancel invitation',
        this.emails.toString(),
        '',
        {
          plan: this.$store.state.currentUser.plan,
          isSelfCreatedTeam: this.$store.state.currentAccount.createdFrom === 'presenter',
        },
      )
      this.$emit('hide')
    },
    contactSupport() {
      window.open('https://form.typeform.com/to/sC0I4AXE', '_blank')
    },
  },
}
</script>
<style scoped lang='scss'>
</style>
